import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { formatDate } from "@/lib/family-date";
import { AlertTriangle, BookOpen, Bell, ClipboardList } from "lucide-react";
import type { DashboardAlert } from "@/hooks/useParentDashboardStatus";

interface Props {
  child: {
    id: string;
    name: string;
    avatar_emoji: string;
  };
  taskDone: number;
  taskTotal: number;
  homeworkCount?: number;
  reminderCount?: number;
  nextHandover?: {
    child_id: string;
    date: string;
  } | null;
  timezone: string;
  isWithOtherParent?: boolean;
  alerts?: DashboardAlert[];
}

const CRITICAL_TYPES = new Set<DashboardAlert["type"]>(["overdue_homework", "overdue_reminder"]);

const ALERT_CONFIG: Record<DashboardAlert["type"], { icon: typeof AlertTriangle; iconColor: string }> = {
  overdue_homework: { icon: AlertTriangle, iconColor: "text-destructive" },
  overdue_reminder: { icon: AlertTriangle, iconColor: "text-destructive" },
  due_today_homework: { icon: BookOpen, iconColor: "text-amber-500 dark:text-amber-400" },
  unseen_reminder: { icon: Bell, iconColor: "text-primary" },
};

export function DashboardChildCard({ child, taskDone, taskTotal, homeworkCount = 0, reminderCount = 0, nextHandover, timezone, isWithOtherParent, alerts = [] }: Props) {
  const navigate = useNavigate();
  const handoverForChild = nextHandover?.child_id === child.id ? nextHandover : null;
  const remaining = taskTotal - taskDone;
  const progress = taskTotal > 0 ? Math.round((taskDone / taskTotal) * 100) : 0;
  const allDone = taskTotal > 0 && taskDone === taskTotal;

  return (
    <Card
      className="cursor-pointer hover:shadow-md transition-all active:scale-[0.97]"
      onClick={() => navigate(`/parent/child/${child.id}`)}
    >
      <CardContent className="p-4 space-y-2.5">
        {/* Top row: avatar + name + chevron */}
        <div className="flex items-center gap-3">
          <span className="text-3xl">{child.avatar_emoji}</span>
          <p className="font-semibold text-base flex-1">{child.name}</p>
          <span className="text-muted-foreground text-lg">›</span>
        </div>

        {/* Alerts */}
        {alerts.length > 0 && !isWithOtherParent && (
        <div className="space-y-1">
            {(() => {
              const firstCriticalIndex = alerts.findIndex((a) => CRITICAL_TYPES.has(a.type));
              return alerts.map((alert, index) => {
                const config = ALERT_CONFIG[alert.type];
                const Icon = config.icon;
                const isCritical = CRITICAL_TYPES.has(alert.type);
                const useRedText = isCritical && index === firstCriticalIndex;
                const textClass = useRedText ? "text-destructive" : "text-foreground";
                return (
                  <div key={alert.type} className="flex items-center gap-1.5 text-xs font-medium">
                    <Icon className={`h-3.5 w-3.5 shrink-0 ${config.iconColor}`} />
                    <span className={textClass}>{alert.count && alert.count > 1 ? `${alert.count} ${alert.label.toLowerCase()}` : alert.label}</span>
                  </div>
                );
              });
            })()}
          </div>
        )}

        {/* Status */}
        {isWithOtherParent ? (
          <p className="text-xs text-muted-foreground">Hos medforelder i dag 🏠</p>
        ) : (
          <>
            {/* Summary chips */}
            {(taskTotal > 0 || homeworkCount > 0 || reminderCount > 0) && (
              <div className="flex flex-wrap gap-1.5">
                {taskTotal > 0 && (
                  <span className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium ${allDone ? "bg-success/15 text-success" : "bg-muted text-muted-foreground"}`}>
                    <ClipboardList className="h-3 w-3" />
                    {taskDone}/{taskTotal}
                  </span>
                )}
                {homeworkCount > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                    <BookOpen className="h-3 w-3" />
                    {homeworkCount} lekse{homeworkCount !== 1 ? "r" : ""}
                  </span>
                )}
                {reminderCount > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                    <Bell className="h-3 w-3" />
                    {reminderCount}
                  </span>
                )}
              </div>
            )}

            {/* Progress bar */}
            {taskTotal > 0 && (
              <Progress
                value={progress}
                className={`h-1 ${allDone ? "[&>div]:bg-success" : ""}`}
              />
            )}

            {/* Status lines */}
            <div className="space-y-0.5">
              {taskTotal > 0 && (
                <p className="text-xs text-muted-foreground">
                  {allDone
                    ? "Ferdig for i dag ✅"
                    : `${remaining} oppgave${remaining !== 1 ? "r" : ""} gjenstår`}
                </p>
              )}
              {taskTotal === 0 && homeworkCount === 0 && reminderCount === 0 && alerts.length === 0 && (
                <p className="text-xs text-muted-foreground">Ingen aktivitet i dag</p>
              )}
              {handoverForChild && (
                <p className="text-xs text-muted-foreground">
                  Neste bytte:{" "}
                  {formatDate(handoverForChild.date + "T12:00:00", timezone, {
                    weekday: "long",
                  })}
                </p>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
